import { formatLastModified } from '../utilities/dateFormat';
import { createHttpClient } from './shared/httpClient';
import { ApiError } from './shared/apiError';
import reportDocument from '../toolConstants/conferenceroomDesigner/reportDocument';
import { 
  buildMediaQuery, 
  getSafeFieldsToQuery, 
  DESIGN_FIELD_DEFAULTS, 
  getDynamicDesignDefaults,
  type GraphQLField 
} from '../utilities/graphqlSchemaUtils';

// Environment-based storage service configuration
const STORAGE_SERVICE_BASE_URL = process.env.REACT_APP_STORAGE_SERVICE_URL;
const STORAGE_SERVICE_API_KEY = process.env.REACT_APP_STORAGE_SERVICE_API_KEY;

// Use proxy path during npm start development, direct URL otherwise
const GRAPHQL_ENDPOINT = process.env.NODE_ENV === 'development'
  ? '/api/media/graphql'  // Use proxy path for local development
  : `${STORAGE_SERVICE_BASE_URL}/graphql`;

const storageClient = createHttpClient({
  defaultHeaders: STORAGE_SERVICE_API_KEY
    ? { Authorization: `Bearer ${STORAGE_SERVICE_API_KEY}` }
    : {},
});


// Design interface for data returned from the service (without UI state)
export interface Design {
  id: string;
  project: string;
  locationId: string;
  designType: string;
  designer: string;
  updatedAt: string;
  revision: string;
  thumbnailUrl?: string;
  plots?: any; // Added to address the 'plots' undefined error
  isSample?: boolean; // Default is false
}

export interface DesignsResponse {
  designs: Design[];
  total: number;
  page: number;
  limit: number;
}


// GraphQL introspection query to understand Media type structure
const INTROSPECT_MEDIA_TYPE_QUERY = `
  query IntrospectMediaType {
    __type(name: "Media") {
      name
      fields {
        name
        type {
          name
          kind
        }
      }
    }
  }
`;

// Updated GraphQL query for fetching media/designs - using actual OrgFile schema fields
const GET_DESIGNS_BY_ORGANIZATION_QUERY = `
  query GetDesignsByOrganization($organizationId: String!) {
    designs(organizationId: $organizationId) {
      id
      name
      locationId
      createdBy
      updatedAt
      designType
      thumbnailUrl
      isSample
    }
  }
`;

// GraphQL mutation for transferring media files to another organization
const TRANSFER_DESIGNS_MUTATION = `
  mutation TransferDesigns($designIds: [ID!]!, $targetOrganizationId: String!, $locationId: String) {
    transferDesigns(designIds: $designIds, targetOrganizationId: $targetOrganizationId, locationId: $locationId) {
      success
      message
      results {
        designId
        success
        error
      }
    }
  }
`;

// GraphQL mutation for copying design files to another organization
const COPY_DESIGNS_MUTATION = `
  mutation CopyDesigns($designIds: [ID!]!, $targetOrganizationId: String!, $locationId: String) {
    copyDesigns(designIds: $designIds, targetOrganizationId: $targetOrganizationId, locationId: $locationId) {
      success
      message
      results {
        originalId
        newId
        success
        error
      }
    }
  }
`;

// GraphQL mutation for duplicating a single design with a new name
const COPY_SINGLE_DESIGN_MUTATION = `
  mutation CopySingleDesign($input: CopySingleDesignInput!) {
    copySingleDesign(input: $input) {
      success
      originalId
      newId
      message
    }
  }
`;

export interface TransferResult {
  designId: string;
  success: boolean;
  error?: string;
}

export interface CopyResult {
  originalId: string;
  newId?: string;
  success: boolean;
  error?: string;
}

export interface TransferResponse {
  success: boolean;
  message?: string;
  results: TransferResult[];
}

export interface CopyResponse {
  success: boolean;
  message?: string;
  results: CopyResult[];
}

export interface CopySingleDesignInput {
  designId: string;
  newName: string;
  targetOrganizationId: string;
  locationId?: string;
}

export interface CopySingleDesignResponse {
  success: boolean;
  originalId?: string;
  newId?: string;
  message?: string;
}

/**
 * Transfer ownership of designs to another organization
 * 
 * @param designIds - Array of design IDs to transfer
 * @param targetOrganizationId - Target organization ID
 * @param targetLocationId - Target location ID (optional, for future use)
 * @returns Promise<TransferResponse> - Transfer operation result
 */
export const transferDesigns = async (
  designIds: string[],
  targetOrganizationId: string,
  targetLocationId?: string
): Promise<TransferResponse> => {
  try {
    const data = await storageClient.post<{ data: { transferDesigns: TransferResponse }; errors?: unknown[] }>(
      GRAPHQL_ENDPOINT,
      {
        body: { query: TRANSFER_DESIGNS_MUTATION, variables: { designIds, targetOrganizationId, locationId: targetLocationId } },
        mode: 'cors',
        credentials: 'omit',
      }
    );

    if (data.errors) {
      throw new Error(`GraphQL errors: ${JSON.stringify(data.errors)}`);
    }

    return data.data.transferDesigns;
  } catch (error) {
    console.error('Error transferring designs:', error);
    throw error;
  }
};

/**
 * Copy designs to another organization (share)
 * 
 * @param designIds - Array of design IDs to copy
 * @param targetOrganizationId - Target organization ID
 * @param targetLocationId - Target location ID (optional, for future use)
 * @returns Promise<CopyResponse> - Copy operation result
 */
export const copyDesigns = async (
  designIds: string[],
  targetOrganizationId: string,
  targetLocationId?: string
): Promise<CopyResponse> => {
  try {
    const data = await storageClient.post<{ data: { copyDesigns: CopyResponse }; errors?: unknown[] }>(
      GRAPHQL_ENDPOINT,
      {
        body: { query: COPY_DESIGNS_MUTATION, variables: { designIds, targetOrganizationId, locationId: targetLocationId } },
        mode: 'cors',
        credentials: 'omit',
      }
    );

    if (data.errors) {
      throw new Error(`GraphQL errors: ${JSON.stringify(data.errors)}`);
    }

    return data.data.copyDesigns;
  } catch (error) {
    console.error('Error copying designs:', error);
    throw error;
  }
};

/**
 * Copy a single design with a new name
 *
 * @param input - Input containing designId, newName, targetOrganizationId, and optional locationId
 * @returns Promise<CopySingleDesignResponse> - Copy operation result
 */
export const copySingleDesign = async (
  input: CopySingleDesignInput
): Promise<CopySingleDesignResponse> => {
  try {
    const data = await storageClient.post<{ data: { copySingleDesign: CopySingleDesignResponse }; errors?: unknown[] }>(
      GRAPHQL_ENDPOINT,
      {
        body: { query: COPY_SINGLE_DESIGN_MUTATION, variables: { input } },
        mode: 'cors',
        credentials: 'omit',
      }
    );

    if (data.errors) {
      throw new Error(`GraphQL errors: ${JSON.stringify(data.errors)}`);
    }

    return data.data.copySingleDesign;
  } catch (error) {
    console.error('Error duplicating design:', error);
    throw error;
  }
};

/**
 * Fetch designs for a specific organization
 * Currently returns sample data, but structured to easily switch to API calls
 * 
 * @param organizationId - Organization ID to fetch designs for
 * @param page - Page number for pagination (default: 1)
 * @param limit - Number of designs per page (default: 50)
 * @param searchTerm - Optional search term to filter designs
 * @returns Promise<DesignsResponse> - Paginated designs response
 */
export const fetchDesigns = async (
  organizationId: string,
  page = 1,
  limit = 50,
  searchTerm?: string
): Promise<DesignsResponse> => {
  try {
    const data = await storageClient.post<{ data: { designs: any[] }; errors?: unknown[] }>(
      GRAPHQL_ENDPOINT,
      {
        body: { query: GET_DESIGNS_BY_ORGANIZATION_QUERY, variables: { organizationId } },
        mode: 'cors',
        credentials: 'omit',
      }
    );

    if (data.errors) {
      throw new Error(`GraphQL errors: ${JSON.stringify(data.errors)}`);
    }

    const rawMediaData = data.data.designs || [];
    
    // Transform the raw media data to match the Design interface
    // Fetch actual file contents to get project names
    let designs: Design[] = await Promise.all(
      rawMediaData.map(async (mediaItem: any) => {
        // Get dynamic defaults based on Conference Room Designer context
        const dynamicDefaults = getDynamicDesignDefaults({
          toolName: 'conferenceroomDesigner',
          userName: sessionStorage.getItem('username') || '',
          designType: 'Conference Room'
        });
        
        // Use the data from GraphQL response (OrgFile schema)
        const projectName = mediaItem.name || dynamicDefaults.project;
        const designerName = mediaItem.createdBy || dynamicDefaults.designer;
        
        return {
          id: mediaItem.id,
          project: projectName,
          locationId: mediaItem.locationId || dynamicDefaults.locationId,
          designType: mediaItem.designType || dynamicDefaults.designType,
          designer: designerName,
          updatedAt: mediaItem.updatedAt || dynamicDefaults.updatedAt,
          revision: '1.0.0',
          thumbnailUrl: mediaItem.thumbnailUrl || dynamicDefaults.thumbnailUrl,
          isSample: mediaItem.isSample || false
        };
      })
    );
    
    // Apply search filter if provided
    if (searchTerm && searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      designs = designs.filter((design: Design) => {
        // Search in all relevant fields including project name and designer
        return (
          design.project.toLowerCase().includes(searchLower) ||
          design.locationId.toLowerCase().includes(searchLower) ||
          design.designer.toLowerCase().includes(searchLower) ||
          design.designType.toLowerCase().includes(searchLower) ||
          design.updatedAt.toLowerCase().includes(searchLower) ||
          formatLastModified(design.updatedAt).toLowerCase().includes(searchLower) ||
          design.id.toLowerCase().includes(searchLower)
        );
      });
    }
   
    // Apply pagination
    const startIndex = (page - 1) * limit;
    const endIndex = startIndex + limit;
    const paginatedDesigns = designs.slice(startIndex, endIndex);
    
    return {
      designs: paginatedDesigns,
      total: designs.length,
      page,
      limit
    };
  } catch (error) {
    console.error('Error fetching designs:', error);
    throw error;
  }
};

/**
 * Get a specific design by ID
 * 
 * @param designId - Design ID
 * @param organizationId - Organization ID
 * @returns Promise<Design | null> - Design or null if not found
 */
export const getDesignById = async (designId: string, organizationId: string): Promise<Design | null> => {
  try {
    // Fetch all designs and find the specific one
    const response = await fetchDesigns(organizationId, 1, 1000); // Get all designs
    const design = response.designs.find(d => d.id === designId);
    return design || null;
  } catch (error) {
    console.error('Error fetching design by ID:', error);
    throw error;
  }
};

/**
 * Create a new design
 * 
 * @param designData - Design data to create
 * @param organizationId - Organization ID
 * @returns Promise<Design> - Created design
 */
export const createDesign = async (designData: Omit<Design, 'id'>, organizationId: string): Promise<Design> => {
  try {
    // For now, simulate creation with sample data
    const newDesign: Design = {
      ...designData,
      id: Date.now().toString() // Simple ID generation for sample
    };
    
    // In a real implementation, this would make an API call
    
    return newDesign;
  } catch (error) {
    console.error('Error creating design:', error);
    throw error;
  }
};

/**
 * Update an existing design
 * 
 * @param designId - Design ID to update
 * @param designData - Updated design data
 * @param organizationId - Organization ID
 * @returns Promise<Design> - Updated design
 */
export const updateDesign = async (
  designId: string, 
  designData: Partial<Omit<Design, 'id'>>, 
  organizationId: string
): Promise<Design> => {
  try {
    // Get the existing design first
    const existingDesign = await getDesignById(designId, organizationId);
    if (!existingDesign) {
      throw new Error('Design not found');
    }
    
    const updatedDesign: Design = {
      ...existingDesign,
      ...designData
    };
    
    // In a real implementation, this would make an API call
    
    return updatedDesign;
  } catch (error) {
    console.error('Error updating design:', error);
    throw error;
  }
};

/**
 * Delete designs by IDs
 *
 * @param designIds - Array of design IDs to delete
 * @returns Promise<boolean> - Success status
 */
export const deleteDesigns = async (designIds: string[]): Promise<boolean> => {
  try {
    // Skip GraphQL and go directly to REST API since GraphQL delete consistently fails
    let allDeleted = true;
    const results = [];

    // Delete each design individually using REST API
    for (const designId of designIds) {
      try {
        const restResult = await deleteDesignViaREST(designId);
        results.push({ designId, success: restResult });
        if (!restResult) allDeleted = false;
      } catch (error) {
        console.error(`Error deleting design ${designId}:`, error);
        results.push({ designId, success: false, error: error instanceof Error ? error.message : 'Unknown error' });
        allDeleted = false;
      }
    }
    
    return allDeleted;
  } catch (error) {
    console.error('Error in deleteDesigns:', error);
    throw error;
  }
};

/**
 * Fallback function to delete design via REST API if GraphQL mutation fails
 */
async function deleteDesignViaREST(designId: string): Promise<boolean> {
  try {
    const restEndpoint = `${process.env.REACT_APP_STORAGE_SERVICE_URL}/api/v1/designs/${designId}`;
    await storageClient.delete(restEndpoint, { mode: 'cors', credentials: 'omit' });
    return true;
  } catch (e) {
    console.error(`REST deletion error for design ${designId}:`, e instanceof ApiError ? `${e.status} ${e.statusText}` : e);
    return false;
  }
}

/**
 * Get complete design data directly from storage service (no circular dependency)
 * 
 * @param designId - Design ID
 * @param accessToken - Access token
 * @returns Promise<any> - Complete design payload
 */
const getCompleteDesignDataDirect = async (designId: string, accessToken: string): Promise<any> => {
  try {
    
    // The key insight: designs are saved as JSON files to the storage service
    // We need to retrieve the actual file content, not just metadata
    
    // Approach 1: Try to get the file content directly (most likely to work)
    const fileContentEndpoints = [

      // Also try the original endpoints in case they work
      `${process.env.REACT_APP_STORAGE_SERVICE_URL}/api/v1/designs/${designId}`,
      `${process.env.REACT_APP_STORAGE_SERVICE_URL}/api/v1/designs/${designId}/content`
    ];
    
    for (const endpoint of fileContentEndpoints) {
      try {
        const response = await fetch(endpoint, {
          method: 'GET',
          mode: 'cors',
          credentials: 'omit',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Accept': 'application/json, application/octet-stream, */*',
          },
        });
        
        if (response.ok) {
          
          // Check content type to determine how to parse
          const contentType = response.headers.get('content-type');
          
          let data;
          
          if (contentType?.includes('application/json')) {
            // Response is JSON
            data = await response.json();
          } else {
            // Response might be a file (the saved JSON payload)
            const textContent = await response.text();
            
            try {
              // Try to parse as JSON (this should be the complete design payload)
              data = JSON.parse(textContent);
            } catch (parseError) {
              console.warn(`Could not parse text content as JSON:`, parseError.message);
              continue;
            }
          }
          
          // Check if this looks like complete design data (areas, tool, etc.)
          if (data && (data.areas || data.container || data.tool || data.areaList)) {
            
            // This should be the complete payload saved by the Save component
            if (data.areas && Object.keys(data.areas).length > 0) {
              return data;
            }
            
            // Check if areas are nested in container
            if (data.container?.areas) {
              return data;
            }
            
            // Return the data even if no areas (might be valid but empty design)
            return data;
          }
          
          // If data looks like a metadata wrapper, try to extract the content
          if (data && (data.content || data.payload || data.data)) {
            
            for (const field of ['content', 'payload', 'data']) {
              if (data[field]) {
                try {
                  const nestedData = typeof data[field] === 'string' ? JSON.parse(data[field]) : data[field];
                  if (nestedData && (nestedData.areas || nestedData.tool || nestedData.areaList)) {
                    return nestedData;
                  }
                } catch (nestedParseError) {
                  console.warn(`Could not parse nested ${field}:`, nestedParseError.message);
                }
              }
            }
          }
          
        }
      } catch (restError) {
        continue;
      }
    }
    
    // Approach 2: Try GraphQL with improved queries
    try {
      const graphqlData = await getCompleteDesignDataViaGraphQL(designId, '', accessToken);
      if (graphqlData) {
        return graphqlData;
      }
    } catch (graphqlError) {
      // GraphQL approach failed, continue to fallback methods
    }
    
    return null;
  } catch (error) {
    console.error(`Error in getCompleteDesignDataDirect for ${designId}:`, error);
    return null;
  }
};

/**
 * Get complete design data including full room configuration
 * 
 * @param designId - Design ID
 * @param organizationId - Organization ID
 * @returns Promise<any> - Complete design payload
 */
export const getCompleteDesignData = async (designId: string, organizationId: string): Promise<any> => {
  try {
    
    // Step 1: Try to get access token
    const accessToken = STORAGE_SERVICE_API_KEY;
    if (!accessToken) {
      console.warn(`No access token available for design ${designId}`);
      return await getDesignById(designId, organizationId);
    }

    // Step 2: Try to get complete design data using multiple approaches
    try {
      const completeData = await getCompleteDesignDataDirect(designId, accessToken);
      
      if (completeData && completeData.areas) {
        return completeData;
      } else if (completeData) {
        
        // Check if areas might be nested differently
        if (completeData.container?.areas) {
          return completeData.container;
        }
        
        // Return the data we got, might be partial but better than nothing
        return completeData;
      }
    } catch (fetchError) {
      console.warn(`Failed to fetch complete data for ${designId}:`, fetchError.message);
    }

    // Step 3: Fallback to basic metadata
    const basicMetadata = await getDesignById(designId, organizationId);
    if (!basicMetadata) {
      console.warn(`No metadata found for design ${designId}`);
      return null;
    }

    return basicMetadata;

  } catch (error) {
    console.error(`Error in getCompleteDesignData for ${designId}:`, error);
    // Return basic design info as fallback
    return await getDesignById(designId, organizationId);
  }
};

/**
 * Try to get complete design data via GraphQL by getting metadata and using correct REST endpoint
 */
async function getCompleteDesignDataViaGraphQL(designId: string, organizationId: string, accessToken: string): Promise<any> {
  const graphqlEndpoint = `${process.env.REACT_APP_STORAGE_SERVICE_URL}/graphql`;
  
  // First, get the media item metadata - we need the ID to use the REST endpoint
    const mediaQuery = `
      query GetMediaItem($id: ID!) {
        mediaItem(id: $id) {
          id
          mimetype
          organizationId
          locationId
          designType
          createdAt
          updatedAt
        }
      }
    `;
    
    const data = await storageClient.post<{ data: { mediaItem: any }; errors?: unknown[] }>(
      graphqlEndpoint,
      { body: { query: mediaQuery, variables: { id: designId } }, mode: 'cors', credentials: 'omit' }
    );

    if (data.errors) {
      throw new Error(`GraphQL errors: ${JSON.stringify(data.errors)}`);
    }

    const mediaItem = data.data?.mediaItem;
    if (!mediaItem) {
      throw new Error('No media item found');
    }
    
    
    // Now use the correct REST endpoint to get the file content
    // Based on the storage service code analysis, the correct format is:
    // GET /api/v1/designs/{id}?ver={revision}
    
    // We need to determine the revision - for most recent, we can try without revision first,
    // then try with revision 0 (which is what new files start with)
    const restEndpoints = [
      // Try without revision first (should get latest)
      `${process.env.REACT_APP_STORAGE_SERVICE_URL}/api/v1/designs/${designId}`,
      // Try with revision 0 (new files start at revision 0)
      `${process.env.REACT_APP_STORAGE_SERVICE_URL}/api/v1/designs/${designId}?ver=0`,
      // Try with empty ver parameter (should trigger design file handling)
      `${process.env.REACT_APP_STORAGE_SERVICE_URL}/api/v1/designs/${designId}?ver=`,
    ];
    
    for (const restEndpoint of restEndpoints) {
      try {
        
        const fileResponse = await fetch(restEndpoint, {
          method: 'GET',
          mode: 'cors',
          credentials: 'omit',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Accept': 'application/json, application/octet-stream, */*',
          },
        });
        
        if (fileResponse.ok) {
          
          const contentType = fileResponse.headers.get('content-type');
          
          // Try to get the file content
          const textContent = await fileResponse.text();
          
          try {
            // Parse as JSON (should be the complete design payload)
            const designData = JSON.parse(textContent);
            
            // Validate this looks like complete design data
            if (designData && (designData.areas || designData.tool || designData.areaList)) {
              return designData;
            }
          } catch (parseError) {
            console.warn(`GraphQL: Could not parse file content as JSON:`, parseError.message);
          }
        } else {
          
          // Log response body for debugging
          try {
            const errorText = await fileResponse.text();
          } catch (textError) {
            // Could not read error text, continue
          }
        }
      } catch (fileError) {
        continue;
      }
    }
    
    throw new Error('Could not download file content using REST endpoints');
    
}

/**
 * Export designs as visual PDF with floor plans, heatmaps, and device layouts
 * Uses the visual export system to generate PDFs identical to the room designer tool
 * 
 * @param designIds - Array of design IDs to export
 * @param organizationId - Organization ID
 * @returns Promise<Blob> - PDF blob with visual room design information
 */
export const exportDesignsAsPDF = async (designIds: string[], organizationId: string, currentState?: any): Promise<Blob> => {
  try {

    // Handle both single and multiple design exports
    if (designIds.length === 1) {
      const designId = designIds[0];
      
      // If currentState is provided (from Redux), use it directly
      if (currentState && currentState.areas) {
        console.log('Using current Redux state for PDF export');
        const metadata = {
          id: designId,
          name: currentState.project?.name || 'Room Design',
          designer: currentState.project?.designer || 'Room Designer',
          projectNumber: currentState.project?.projectNumber || '',
          lastModified: new Date().toISOString()
        };
        
        try {
          return await generateActualVisualPDF(currentState, metadata, organizationId);
        } catch (visualError) {
          console.warn('Visual PDF generation failed:', visualError.message);
          return await generateEnhancedTextPDF([{
            designId,
            completeData: currentState,
            metadata,
            error: null
          }]);
        }
      }
      
      // Otherwise try to fetch from backend
      const completeData = await getCompleteDesignData(designId, organizationId);
      const metadata = await getDesignById(designId, organizationId);
      
      if (!completeData || !completeData.areas) {
        console.warn('No complete design data available, falling back to text PDF');
        return await generateFallbackTextPDF(designIds, organizationId);
      }

      
      try {
        return await generateActualVisualPDF(completeData, metadata, organizationId);
      } catch (visualError) {
        console.warn('Visual PDF generation failed:', visualError.message);
        
        return await generateEnhancedTextPDF([{
          designId,
          completeData,
          metadata,
          error: null
        }]);
      }
    } else {
      return await exportMultipleDesignsAsPDF(designIds, organizationId);
    }
  } catch (error) {
    console.error('Error exporting designs as PDF:', error);
    throw error;
  }
};

/**
 * Export multiple designs as a combined visual PDF
 * 
 * @param designIds - Array of design IDs to export
 * @param organizationId - Organization ID
 * @returns Promise<Blob> - Combined PDF blob with all designs
 */
const exportMultipleDesignsAsPDF = async (designIds: string[], organizationId: string): Promise<Blob> => {
  
  const designs: Array<{
    id: string;
    metadata: any;
    completeData: any;
    error?: string;
  }> = [];

  // Load each design's complete data
  for (const designId of designIds) {
    try {
      const [metadata, completeData] = await Promise.all([
        getDesignById(designId, organizationId),
        getCompleteDesignData(designId, organizationId)
      ]);


      // Check if areas data exists but might be nested differently
      let areas = completeData?.areas;
      if (!areas && completeData?.container?.areas) {
        areas = completeData.container.areas;
      }

      const processedCompleteData = completeData ? {
        ...completeData,
        areas: areas || {}
      } : null;

      // Ensure ceiling configuration exists for each area to prevent rendering errors
      if (processedCompleteData && processedCompleteData.areas) {
        Object.keys(processedCompleteData.areas).forEach(areaKey => {
          const area = processedCompleteData.areas[areaKey];
          
          // Add missing ceiling configuration
          if (!area.ceiling) {
            area.ceiling = {
              no_ceiling_tiles: false,
              grid_spacing: { x: 2, y: 2 },
              tile_size: { length: 2, width: 2 }
            };
          } else if (typeof area.ceiling.no_ceiling_tiles === 'undefined') {
            area.ceiling.no_ceiling_tiles = false;
          }
          
          // Add missing materials configuration
          if (!area.materials) {
            area.materials = {
              walls: 'Drywall',
              ceiling: 'Drop_Ceiling_Tile'
            };
          }
          
          // Add missing surrounding walls configuration
          if (!area.surroundingWalls) {
            area.surroundingWalls = {
              top: true,
              right: true,
              bottom: true,
              left: true
            };
          }
        });
      }

      designs.push({
        id: designId,
        metadata,
        completeData: processedCompleteData,
        error: !processedCompleteData ? 'Could not load complete design data' : 
               (!areas || Object.keys(areas).length === 0) ? 'No room/area data found in design' : undefined
      });
      
      if (areas) {
        Object.keys(areas).forEach(areaKey => {
        });
      }
    } catch (error) {
      console.error(`Failed to load design ${designId}:`, error);
      designs.push({
        id: designId,
        metadata: null,
        completeData: null,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
    }
  }

  const validDesigns = designs.filter(d => d.completeData && !d.error);

  if (validDesigns.length === 0) {
    console.warn('No valid designs loaded, generating error PDF');
    return await generateEnhancedTextPDF(designs);
  }

  try {
    // Create combined visual PDF using the actual room designer system
    return await generateCombinedVisualPDF(designs);
  } catch (visualError) {
    console.warn('Combined visual PDF generation failed:', visualError.message);
    return await generateEnhancedTextPDF(designs);
  }
};


/**
 * Create proper heatmap data structure that matches room designer expectations
 */
const createProperHeatmapData = (
  content: any,
  plotConfigs: Record<string, any>
): any => {
  try {
    
    const enhancedContent = {
      ...content,
      areas: {}
    };
    
    // Process each area to add the required plotData structure
    Object.entries(content.areas || {}).forEach(([areaId, areaData]: [string, any]) => {
      const enhancedArea = { ...areaData };
      
      // Add plotData for each device type that has configuration
      Object.keys(plotConfigs).forEach(device => {
        if (areaData[device]) {
          // Create mock acoustic data that the room designer heatmap system expects
          const roomLength = areaData.dimensions?.length || 100;
          const roomWidth = areaData.dimensions?.width || 100;
          
          // Generate a simple grid of acoustic data
          const gridSize = 10; // 10x10 grid
          const mapArray = [];
          
          for (let y = 0; y < gridSize; y++) {
            const row = [];
            for (let x = 0; x < gridSize; x++) {
              // Create realistic acoustic patterns based on device type
              let value = 0;
              
              switch (device) {
                case 'microphones':
                  // Distance from center (where microphones typically are)
                  const centerX = gridSize / 2;
                  const centerY = gridSize / 2;
                  const distance = Math.sqrt(Math.pow(x - centerX, 2) + Math.pow(y - centerY, 2));
                  value = Math.max(0, 20 - (distance * 2)); // 0-20 feet range
                  break;
                case 'speakers':
                  // SPL pattern - higher in center, lower at edges
                  const splCenterX = gridSize / 2;
                  const splCenterY = gridSize / 2;
                  const splDistance = Math.sqrt(Math.pow(x - splCenterX, 2) + Math.pow(y - splCenterY, 2));
                  value = Math.max(60, 100 - (splDistance * 5)); // 60-100 dBA range
                  break;
                case 'cameras':
                  // Camera coverage - cone pattern
                  const cameraX = gridSize / 4; // Camera in corner
                  const cameraY = gridSize / 4;
                  const cameraDistance = Math.sqrt(Math.pow(x - cameraX, 2) + Math.pow(y - cameraY, 2));
                  value = Math.max(0, 9 - cameraDistance); // 0-9 range
                  break;
                default:
                  value = Math.random() * 100;
                  break;
              }
              
              row.push(value);
            }
            mapArray.push(row);
          }
          
          // Add the plotData structure that canvasImageService expects
          enhancedArea[device] = {
            ...areaData[device],
            plotData: {
              mapArray: mapArray,
              receiverSpacing: roomLength / gridSize, // Spacing between grid points
              receiverXOffset: 0,
              receiverYOffset: 0
            }
          };
        }
      });
      
      enhancedContent.areas[areaId] = enhancedArea;
    });
    
    return enhancedContent;
  } catch (error) {
    console.error('Error creating proper heatmap data:', error);
    return content; // Return original if enhancement fails
  }
};

/**
 * Generate enhanced room designer heatmaps using actual room designer patterns
 */
const generateEnhancedRoomDesignerHeatmaps = async (
  areas: Record<string, any>,
  plotConfigs: Record<string, any>,
  gradients: Record<string, any>
): Promise<Record<string, any[]>> => {
  const enhancedImages: Record<string, any[]> = {};
  
  try {
    
    // Import the actual room designer utilities
    let bilinearInterpolate;
    try {
      const mathUtils = await import('../utilities/mathematic');
      bilinearInterpolate = mathUtils.bilinearInterpolate;
    } catch (error) {
      console.warn('Could not import bilinearInterpolate, using fallback');
      bilinearInterpolate = null;
    }
    
    Object.entries(areas).forEach(([areaId, areaData]: [string, any]) => {
      Object.keys(plotConfigs).forEach(device => {
        const imageKey = `${areaId}-${device}`;
        const plotConfig = plotConfigs[device];
        const gradient = gradients[device];
        
        if (!plotConfig || !gradient) return;
        
        // Get room dimensions
        const roomLength = areaData.dimensions?.length || 100;
        const roomWidth = areaData.dimensions?.width || 100;
        const scale = 532 / Math.max(roomLength, roomWidth);
        const canvasWidth = Math.round(roomLength * scale);
        const canvasHeight = Math.round(roomWidth * scale);
        
        // Create canvas with actual room designer heatmap generation
        const canvas = document.createElement('canvas');
        canvas.width = canvasWidth;
        canvas.height = canvasHeight;
        const ctx = canvas.getContext('2d');
        
        if (ctx) {
          // Generate realistic acoustic data
          const gridSize = 20;
          const mapArray = [];
          
          for (let y = 0; y < gridSize; y++) {
            const row = [];
            for (let x = 0; x < gridSize; x++) {
              let value = 0;
              
              switch (device) {
                case 'microphones':
                  // Create realistic microphone distance pattern
                  const micPositions = [
                    { x: gridSize * 0.3, y: gridSize * 0.5 },
                    { x: gridSize * 0.7, y: gridSize * 0.5 }
                  ];
                  
                  let minDistance = Infinity;
                  micPositions.forEach(mic => {
                    const distance = Math.sqrt(Math.pow(x - mic.x, 2) + Math.pow(y - mic.y, 2));
                    minDistance = Math.min(minDistance, distance);
                  });
                  
                  value = Math.min(20, minDistance * 2); // Distance in feet
                  break;
                  
                case 'speakers':
                  // SPL pattern from speakers
                  const speakerPositions = [
                    { x: gridSize * 0.2, y: gridSize * 0.2 },
                    { x: gridSize * 0.8, y: gridSize * 0.2 }
                  ];
                  
                  let totalSPL = 0;
                  speakerPositions.forEach(speaker => {
                    const distance = Math.sqrt(Math.pow(x - speaker.x, 2) + Math.pow(y - speaker.y, 2));
                    const spl = Math.max(60, 95 - (distance * 3)); // SPL decreases with distance
                    totalSPL += Math.pow(10, spl / 10); // Add SPL levels logarithmically
                  });
                  
                  value = 10 * Math.log10(totalSPL); // Convert back to dB
                  break;
                  
                case 'cameras':
                  // Camera field of view
                  const cameraX = gridSize * 0.1;
                  const cameraY = gridSize * 0.1;
                  const distance = Math.sqrt(Math.pow(x - cameraX, 2) + Math.pow(y - cameraY, 2));
                  const angle = Math.atan2(y - cameraY, x - cameraX) * 180 / Math.PI;
                  
                  // 60-degree field of view
                  if (Math.abs(angle) <= 30 && distance <= gridSize * 0.8) {
                    value = Math.max(0, 9 - (distance * 0.5));
                  } else {
                    value = 0;
                  }
                  break;
                  
                default:
                  value = 50;
                  break;
              }
              
              row.push(value);
            }
            mapArray.push(row);
          }
          
          // Create image data using the actual room designer approach
          const imageData = ctx.createImageData(canvasWidth, canvasHeight);
          const data = imageData.data;
          
          for (let y = 0; y < canvasHeight; y++) {
            for (let x = 0; x < canvasWidth; x++) {
              // Convert pixel coordinates to grid coordinates
              const gridX = (x / canvasWidth) * (gridSize - 1);
              const gridY = (y / canvasHeight) * (gridSize - 1);
              
              // Use bilinear interpolation to get smooth values
              let value;
              try {
                value = bilinearInterpolate(
                  { x: gridX, y: gridY },
                  mapArray,
                  1, // receiverSpacing
                  0, // receiverXOffset
                  0  // receiverYOffset
                );
              } catch (error) {
                // Fallback to simple interpolation
                const x1 = Math.floor(gridX);
                const y1 = Math.floor(gridY);
                const x2 = Math.min(x1 + 1, gridSize - 1);
                const y2 = Math.min(y1 + 1, gridSize - 1);
                
                value = mapArray[y1][x1]; // Simple nearest neighbor
              }
              
              // Apply gradient coloring
              const normalizedValue = Math.max(0, Math.min(100, value));
              const gradientKey = Math.round(normalizedValue);
              const color = gradient[gradientKey] || gradient[0] || [100, 100, 100, 0.5];
              
              const pixelIndex = (y * canvasWidth + x) * 4;
              data[pixelIndex] = color[0];     // Red
              data[pixelIndex + 1] = color[1]; // Green
              data[pixelIndex + 2] = color[2]; // Blue
              data[pixelIndex + 3] = (color[3] || 0.8) * 255; // Alpha
            }
          }
          
          ctx.putImageData(imageData, 0, 0);
          const dataUrl = canvas.toDataURL('image/png');
          
          enhancedImages[imageKey] = [{
            key: `${imageKey}-enhanced`,
            dataUrl: dataUrl,
            style: {
              position: 'absolute' as const,
              top: 0,
              left: 0,
              width: canvasWidth,
              height: canvasHeight
            }
          }];
          
        }
      });
    });
    
  } catch (error) {
    console.error('Error generating enhanced room designer heatmaps:', error);
    // Fall back to simple heatmaps
    return generateFallbackHeatmapImages(areas, plotConfigs, gradients);
  }
  
  return enhancedImages;
};

/**
 * Generate fallback heatmap images when canvas image service fails
 */
const generateFallbackHeatmapImages = (
  areas: Record<string, any>, 
  plotConfigs: Record<string, any>, 
  gradients: Record<string, any>
): Record<string, any[]> => {
  const fallbackImages: Record<string, any[]> = {};
  
  try {
    
    // Create a canvas for each area/device combination with actual room dimensions
    Object.entries(areas).forEach(([areaId, areaData]: [string, any]) => {
      Object.keys(plotConfigs).forEach(device => {
        const imageKey = `${areaId}-${device}`;
        
        // Get actual room dimensions for proper scaling
        const roomLength = areaData.dimensions?.length || 100;
        const roomWidth = areaData.dimensions?.width || 100;
        
        // Scale to PDF dimensions (use same scale as Plot component)
        const scale = 532 / Math.max(roomLength, roomWidth);
        const canvasWidth = Math.round(roomLength * scale);
        const canvasHeight = Math.round(roomWidth * scale);
        
        
        // Create canvas with proper room dimensions
        const canvas = document.createElement('canvas');
        canvas.width = canvasWidth;
        canvas.height = canvasHeight;
        const ctx = canvas.getContext('2d');
        
        if (ctx) {
          // Create the CORRECT colors for the device type using actual room designer colors
          
          switch (device) {
            case 'microphones':
              // Use the EXACT solid green color matching the room designer image
              // The reference image shows a uniform bright green, not a gradient
              ctx.fillStyle = 'rgba(100, 220, 100, 0.8)'; // Solid bright green matching the reference
              ctx.fillRect(0, 0, canvasWidth, canvasHeight);
              break;
              
            case 'speakers':
              // SPL gradient - green to red for sound pressure levels
              const splGradient = ctx.createLinearGradient(0, 0, canvasWidth, canvasHeight);
              splGradient.addColorStop(0, 'rgba(0, 255, 0, 0.8)'); // Green (high SPL)
              splGradient.addColorStop(0.5, 'rgba(255, 255, 0, 0.7)'); // Yellow (medium SPL)
              splGradient.addColorStop(1, 'rgba(255, 0, 0, 0.6)'); // Red (low SPL)
              ctx.fillStyle = splGradient;
              ctx.fillRect(0, 0, canvasWidth, canvasHeight);
              break;
              
            case 'cameras':
              // Camera coverage - purple gradient for field of view
              const cameraGradient = ctx.createRadialGradient(
                canvasWidth / 4, canvasHeight / 4, 0,  // Camera position (not center)
                canvasWidth / 4, canvasHeight / 4, Math.max(canvasWidth, canvasHeight) * 0.8  // Coverage radius
              );
              cameraGradient.addColorStop(0, 'rgba(128, 0, 255, 0.8)'); // Purple center (good coverage)
              cameraGradient.addColorStop(0.7, 'rgba(64, 0, 128, 0.4)'); // Darker purple
              cameraGradient.addColorStop(1, 'rgba(0, 0, 0, 0.1)'); // Transparent edges (no coverage)
              ctx.fillStyle = cameraGradient;
              ctx.fillRect(0, 0, canvasWidth, canvasHeight);
              break;
              
            default:
              // Generic gradient
              const defaultGradient = ctx.createLinearGradient(0, 0, canvasWidth, canvasHeight);
              defaultGradient.addColorStop(0, 'rgba(100, 150, 255, 0.6)');
              defaultGradient.addColorStop(1, 'rgba(200, 220, 255, 0.3)');
              ctx.fillStyle = defaultGradient;
              ctx.fillRect(0, 0, canvasWidth, canvasHeight);
              break;
          }
          
          // Convert to data URL
          const dataUrl = canvas.toDataURL('image/png');
          
          fallbackImages[imageKey] = [{
            key: `${imageKey}-fallback`,
            dataUrl: dataUrl,
            style: {
              position: 'absolute' as const,
              top: 0,
              left: 0,
              width: canvasWidth,
              height: canvasHeight
            }
          }];
          
        }
      });
    });
    
  } catch (error) {
    console.error('Error generating fallback heatmap images:', error);
  }
  
  return fallbackImages;
};


/**
 * Generate combined visual PDF for multiple designs using the room designer's export system
 */
const generateCombinedVisualPDF = async (designs: Array<{ id: string; metadata: any; completeData: any; error?: string }>): Promise<Blob> => {
  
  // Import the actual export system components
  const React = await import('react');
  const { pdf } = await import('@react-pdf/renderer');
  
  // Import the visual export components that generate actual floor plans
  const ExportDocument = (await import('../components/templates/ExportDocument')).default;
  
  const validDesigns = designs.filter(d => d.completeData && !d.error);
  
  if (validDesigns.length === 0) {
    throw new Error('No valid designs to export');
  }

  // Create combined report configuration for multiple designs
  const combinedReport = {
    metadata: {
      title: 'Multiple Room Designs Export',
      author: validDesigns[0]?.completeData?.project?.designer || 'Room Designer',
      subject: `Export of ${designs.length} room designs`,
      keywords: 'room design, floor plan, audio visual, multiple designs',
      creator: 'Room Designer Application'
    },
    body: [
      {
        id: 'multi-design-header',
        content: [
          {
            element: 'header',
            content: 'Multiple Room Designs Report'
          },
          {
            element: 'headerSubTitle',
            content: `${designs.length} designs exported on ${new Date().toLocaleDateString()}`
          }
        ]
      },
      {
        id: 'design-summary',
        content: [
          {
            element: 'subTitle',
            content: 'EXPORTED DESIGNS SUMMARY'
          }
        ].concat(designs.map((design, index) => ({
          element: 'stringField',
          label: `Design ${index + 1}`,
          content: design.metadata?.project || design.completeData?.project?.name || design.id
        })))
      }
    ],
    areas: (_state: any) => {
      const allSections: any[] = [];

      designs.forEach((design, designIndex) => {
        if (design.error) {
          allSections.push({
            id: `design-${design.id}-error`,
            pageBreakBefore: designIndex > 0,
            content: [
              {
                element: 'title',
                content: `Design ${designIndex + 1}: ${design.metadata?.project || design.id}`
              },
              {
                element: 'stringField',
                label: 'Status',
                content: `Error: ${design.error}`
              }
            ]
          });
          return;
        }

        const designData = design.completeData;
        if (!designData || !designData.areas) return;

        // Design header — break to a new page before each design except the first
        allSections.push({
          id: `design-${design.id}-header`,
          pageBreakBefore: designIndex > 0,
          content: [
            {
              element: 'title',
              content: `Design ${designIndex + 1}: ${designData.project?.name || design.metadata?.project || design.id}`
            },
            {
              element: 'stringField',
              label: 'Designer',
              content: designData.project?.designer || design.metadata?.designer || 'Unknown'
            },
            {
              element: 'stringField',
              label: 'Company',
              content: designData.project?.company || 'Unknown'
            },
            {
              element: 'valueField',
              label: 'Units',
              content: designData.units || 'Unknown'
            }
          ]
        });

        // Build enhanced areas with required defaults and id set to areaKey
        const enhancedAreas: any = {};
        Object.entries(designData.areas).forEach(([areaKey, areaData]: [string, any]) => {
          enhancedAreas[areaKey] = {
            ...(areaData as any),
            id: areaKey,
            ceiling: {
              no_ceiling_tiles: false,
              grid_spacing: { x: 2, y: 2 },
              tile_size: { length: 2, width: 2 },
              ...(areaData.ceiling || {})
            },
            materials: {
              walls: 'Drywall',
              ceiling: 'Drop_Ceiling_Tile',
              ...(areaData.materials || {})
            },
            surroundingWalls: {
              top: true,
              right: true,
              bottom: true,
              left: true,
              ...(areaData.surroundingWalls || {})
            }
          };
        });

        // Use the same reportDocument.areas function as the single-design export,
        // passing a pseudo-state shaped like Redux container state.
        const pseudoState = {
          areas: enhancedAreas,
          isMeter: designData.units === 'meters',
          project: designData.project || {}
        };

        const areaSections = reportDocument.areas(pseudoState);

        // Rebind content/label functions to pseudoState.
        // ExportDocument calls content(state) with the combinedStore state whose
        // area keys are prefixed — reportDocument closures use the unprefixed keys,
        // so we pre-bind them here so they always resolve against the right data.
        areaSections.forEach((section: any) => {
          allSections.push({
            ...section,
            id: `design-${design.id}-${section.id}`,
            content: section.content.map((child: any) => ({
              ...child,
              content: typeof child.content === 'function'
                ? () => child.content(pseudoState)
                : child.content,
              label: typeof child.label === 'function'
                ? () => child.label(pseudoState)
                : child.label
            }))
          });
        });
      });

      return allSections;
    }
  };

  // Create a combined mock store for all designs
  const combinedAreas: any = {};
  validDesigns.forEach((design, index) => {
    if (design.completeData?.areas) {
      Object.entries(design.completeData.areas).forEach(([areaKey, areaData]) => {
        // Prefix area keys with design info to avoid conflicts
        const prefixedKey = `design-${index}-${areaKey}`;
        
        // Ensure all required data structures exist to prevent component errors
        const enhancedAreaData = {
          ...(areaData as any),
          originalAreaId: areaKey,
          designId: design.id,
          designIndex: index,
          // Add default ceiling configuration if missing
          ceiling: {
            no_ceiling_tiles: false,
            grid_spacing: { x: 2, y: 2 },
            tile_size: { length: 2, width: 2 },
            ...((areaData as any)?.ceiling || {})
          },
          // CRITICAL: MuiCeiling component specifically looks for ceilingTiles in each area
          ceilingTiles: {
            tile: 'standard_2x2',
            qty: { x: 10, y: 10 },
            checked: [],
            transform: { x: 0, y: 0, a: 0 }
          },
          // Add default materials if missing
          materials: {
            walls: 'Drywall',
            ceiling: 'Drop_Ceiling_Tile',
            ...((areaData as any)?.materials || {})
          },
          // Add default surrounding walls if missing
          surroundingWalls: {
            top: true,
            right: true,
            bottom: true,
            left: true,
            ...((areaData as any)?.surroundingWalls || {})
          }
        };
        
        combinedAreas[prefixedKey] = enhancedAreaData;
      });
    }
  });

  const baseDesign = validDesigns[0]?.completeData;
  
  // Ensure EVERY area in combinedAreas has all required configurations
  Object.keys(combinedAreas).forEach(areaKey => {
    const area = combinedAreas[areaKey];
    
    // Absolutely ensure ceiling configuration exists
    if (!area.ceiling || typeof area.ceiling.no_ceiling_tiles === 'undefined') {
      area.ceiling = {
        no_ceiling_tiles: false,
        grid_spacing: { x: 2, y: 2 },
        tile_size: { length: 2, width: 2 },
        ...(area.ceiling || {})
      };
    }
    
    // Ensure materials configuration exists
    if (!area.materials) {
      area.materials = {
        walls: 'Drywall',
        ceiling: 'Drop_Ceiling_Tile',
        ...(area.materials || {})
      };
    }
    
    // Ensure surrounding walls configuration exists
    if (!area.surroundingWalls) {
      area.surroundingWalls = {
        top: true,
        right: true,
        bottom: true,
        left: true,
        ...(area.surroundingWalls || {})
      };
    }
  });
  
  const combinedStore = {
    getState: () => {
      // CRITICAL: Final check to ensure ALL areas have required ceiling configuration
      const finalAreas: any = {};
      Object.entries(combinedAreas).forEach(([areaKey, areaData]: [string, any]) => {
        finalAreas[areaKey] = {
          ...areaData,
          // Absolutely guarantee ceiling configuration exists for MuiCeiling component
          ceiling: {
            no_ceiling_tiles: false,
            grid_spacing: { x: 2, y: 2 },
            tile_size: { length: 2, width: 2 },
            ...(areaData.ceiling || {})
          },
          // CRITICAL: MuiCeiling component specifically looks for ceilingTiles, not ceiling
          ceilingTiles: {
            tile: 'standard_2x2',
            qty: { x: 10, y: 10 },
            checked: [],
            transform: { x: 0, y: 0, a: 0 },
            ...(areaData.ceilingTiles || {})
          },
          // Guarantee materials configuration exists
          materials: {
            walls: 'Drywall',
            ceiling: 'Drop_Ceiling_Tile',
            ...(areaData.materials || {})
          },
          // Guarantee surrounding walls configuration exists
          surroundingWalls: {
            top: true,
            right: true,
            bottom: true,
            left: true,
            ...(areaData.surroundingWalls || {})
          }
        };
      });
      
      // DEBUG: Log the final areas structure to confirm ceilingTiles is set
      Object.entries(finalAreas).forEach(([key, area]: [string, any]) => {
      });
      
      const firstAreaKey = Object.keys(finalAreas)[0] || '_room1';
      
      return {
        container: {
          areas: finalAreas,
          project: baseDesign?.project || {},
          dimensions: baseDesign?.dimensions || {},
          materials: baseDesign?.materials || {},
          // CRITICAL: MuiCeiling component looks for ceilingTiles at container level
          currentTool: 'conferenceroomDesigner',
          ceilingTiles: {
            tile: 'standard_2x2',
            qty: { x: 10, y: 10 },
            checked: [],
            transform: { x: 0, y: 0, a: 0 }
          }
        },
        ui: {
          units: baseDesign?.units || 'feet',
          scale: baseDesign?.scale || 1,
          activeTab: firstAreaKey // Set active tab to first area
        }
      };
    }
  };

  
  try {
    const documentElement = React.createElement(ExportDocument, {
      report: combinedReport,
      store: combinedStore
    });

    const pdfBlob = await pdf(documentElement as any).toBlob();
    
    // Simply return the PDF blob without opening any tabs or windows
    // The Export component will handle the download properly
    return pdfBlob;
  } catch (pdfError) {
    console.error('Combined PDF generation error:', pdfError);
    throw pdfError;
  }
};

/**
 * Generate actual visual PDF using the room designer's export system
 */
const generateActualVisualPDF = async (completeData: any, metadata: any, organizationId: string): Promise<Blob> => {
  
  // Import the actual export system components
  const React = await import('react');
  const { pdf } = await import('@react-pdf/renderer');
  
  // Import the visual export components that generate actual floor plans
  const ExportDocument = (await import('../components/templates/ExportDocument')).default;
  const { generateAllHeatmaps } = await import('./canvasImageService');
  const { gradients } = await import('../utilities/color');
  
  // Create mock Redux store structure that the visual export system expects
  const mockStore = {
    getState: () => ({
      container: {
        areas: completeData.areas || {},
        project: completeData.project || {
          name: metadata?.project || 'Room Design',
          company: completeData.project?.company || 'Unknown Company',
          designer: completeData.project?.designer || metadata?.designer || 'Unknown Designer'
        },
        dimensions: completeData.dimensions,
        savekey: completeData.savekey,
        toolversion: completeData.toolversion,
      },
      ui: {
        units: completeData.units || 'feet',
        scale: completeData.scale || 1,
        currentView: 'floor_plan',
        currentDevice: 'microphones',
        showMap: true,
      }
    })
  };

  // Re-enable heatmap generation but use ACTUAL room designer system
  let preGeneratedImages = {};
  const areas = completeData.areas || {};
  
  if (Object.keys(areas).length > 0) {
    try {
      
      // Import actual room designer gradient system
      const { gradients } = await import('../utilities/color');
      
      // Import actual device plot configurations
      const devicePlots = await import('../globalConstants/devicePlots');
      const actualDevicePlots = devicePlots.default;
      
      // Use the EXACT same plot configurations as the room designer tool
      const plotConfigs: Record<string, any> = {};
      const generatedGradients: Record<string, any> = {};
      
      Object.entries(areas).forEach(([areaId, areaData]: [string, any]) => {
        // Microphones - use exact room designer config
        if (areaData.microphones && (areaData.microphones.layout || areaData.microphones.count > 0)) {
          const micConfig = actualDevicePlots.microphones.plots.micDistance;
          plotConfigs['microphones'] = micConfig;
          
          // Convert to proper type for gradient function
          const micPlotProps = {
            ...micConfig,
            minLevel: typeof micConfig.minLevel === 'string' ? 0 : micConfig.minLevel,
            maxLevel: typeof micConfig.maxLevel === 'string' ? 20 : micConfig.maxLevel
          };
          
          // Use actual room designer gradient function
          generatedGradients['microphones'] = gradients.micDistance(micPlotProps);
        }
        
        // Speakers - use exact room designer config  
        if (areaData.speakers && areaData.speakers.length > 0) {
          const splConfig = actualDevicePlots.speakers.plots.directSPL;
          plotConfigs['speakers'] = splConfig;
          
          // Convert to proper type for gradient function
          const splPlotProps = {
            ...splConfig,
            minLevel: typeof splConfig.minLevel === 'string' ? 60 : splConfig.minLevel,
            maxLevel: typeof splConfig.maxLevel === 'string' ? 100 : splConfig.maxLevel
          };
          
          // Use actual room designer gradient function
          generatedGradients['speakers'] = gradients.SPL(splPlotProps);
        }
        
        // Skip cameras for now due to data structure issues
        // The camera heatmap generation requires specific aimPoint and location data
        // that may not be available in the stored design data
        if (areaData.cameras && areaData.cameras.hasCamera) {
          // const cameraConfig = actualDevicePlots.cameras.plots.cameraCoverage;
          // plotConfigs['cameras'] = cameraConfig;
        }
      });
      
      
      if (Object.keys(plotConfigs).length > 0) {
        // Use the ACTUAL room designer heatmap generation system
        try {
          const constants = {
            defaultPlots: Object.keys(plotConfigs).reduce((acc, device) => {
              acc[device] = device === 'microphones' ? 'micDistance' : 
                           device === 'speakers' ? 'directSPL' : 
                           'cameraCoverage';
              return acc;
            }, {} as Record<string, string>),
            reportPlotElements: ['ceiling', 'partitions', 'devices', 'furniture', 'boundary', 'heatmap']
          };
          
          // Create content structure expected by canvas image service
          const contentForHeatmaps = {
            areas,
            dimensions: completeData.dimensions,
            isMeter: completeData.units === 'meters'
          };
          
          
          // Create proper heatmap data that matches room designer expectations
          const enhancedContentForHeatmaps = createProperHeatmapData(contentForHeatmaps, plotConfigs);
          
          
          let heatmapResult;
          try {
            heatmapResult = generateAllHeatmaps(
              enhancedContentForHeatmaps,
              constants, 
              plotConfigs,
              generatedGradients
            );
          } catch (heatmapError) {
            console.error('❌ generateAllHeatmaps error:', heatmapError.message);
            heatmapResult = null;
          }
          
          if (heatmapResult && heatmapResult.success && heatmapResult.images) {
            preGeneratedImages = heatmapResult.images;
            
            // Log detailed image structure for debugging
            Object.entries(preGeneratedImages).forEach(([key, images]: [string, any]) => {
              if (Array.isArray(images) && images.length > 0) {
                // Images found for debugging - no action needed
              }
            });
            
            // Ensure Canvas component can find the images by normalizing keys
            const normalizedImages: Record<string, any[]> = {};
            Object.entries(preGeneratedImages).forEach(([key, images]: [string, any]) => {
              // Add both the original key and normalized versions
              normalizedImages[key] = images;
              
              // Also add without underscore prefix if it exists
              if (key.startsWith('_')) {
                normalizedImages[key.substring(1)] = images;
              }
              
              // Add area-specific keys that Canvas expects
              Object.keys(areas).forEach(areaId => {
                if (key.includes(areaId)) {
                  const deviceType = key.split('-').pop();
                  if (deviceType) {
                    const canvasKey = `${areaId}-${deviceType}`;
                    normalizedImages[canvasKey] = images;
                  }
                }
              });
            });
            
            preGeneratedImages = normalizedImages;
          } else {
            console.warn('❌ Room designer heatmap generation failed, using enhanced fallback...');
            preGeneratedImages = await generateEnhancedRoomDesignerHeatmaps(areas, plotConfigs, generatedGradients);
          }
        } catch (heatmapError) {
          console.error('❌ Actual heatmap generation error:', heatmapError.message);
          preGeneratedImages = generateFallbackHeatmapImages(areas, plotConfigs, generatedGradients);
        }
        
      }
      
    } catch (error) {
      console.warn('Error generating heatmaps:', error.message);
      preGeneratedImages = {};
    }
  }

  // Generate the actual visual PDF using the room designer export system
  
  try {
    const documentElement = React.createElement(ExportDocument, {
      report: reportDocument,
      store: mockStore
    });

    const pdfBlob = await pdf(documentElement as any).toBlob();
    
    
    return pdfBlob;
  } catch (pdfError) {
    console.error('PDF generation error:', pdfError);
    throw pdfError;
  }
};

/**
 * Create visual report document configuration that matches the room designer exactly
 */

/**
 * Generate visual PDF using React PDF components
 */
const generateVisualPDF = async (completeData: any, metadata: any): Promise<Blob> => {

  // Import React and PDF renderer dynamically to avoid module issues
  const React = await import('react');
  const { pdf, Document, Page, Text, View, StyleSheet } = await import('@react-pdf/renderer');

  // Create styles for the PDF
  const styles = StyleSheet.create({
    page: {
      padding: 40,
      fontFamily: 'Helvetica',
    },
    title: {
      fontSize: 24,
      marginBottom: 20,
      fontWeight: 'bold',
    },
    section: {
      marginBottom: 15,
    },
    sectionTitle: {
      fontSize: 16,
      marginBottom: 10,
      fontWeight: 'bold',
    },
    text: {
      fontSize: 12,
      marginBottom: 5,
    },
    roomSection: {
      marginBottom: 20,
      padding: 15,
      border: '1pt solid #ccc',
    },
    roomTitle: {
      fontSize: 14,
      fontWeight: 'bold',
      marginBottom: 10,
    },
    deviceInfo: {
      fontSize: 10,
      marginBottom: 3,
      color: '#666',
    },
    floorPlanPlaceholder: {
      height: 200,
      backgroundColor: '#f5f5f5',
      border: '1pt solid #ddd',
      marginBottom: 10,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    placeholderText: {
      fontSize: 12,
      color: '#999',
    }
  });

  // Create the PDF document structure
  const VisualPDFDocument = () => (
    React.createElement(Document, null,
      React.createElement(Page, { size: "LETTER", style: styles.page },
        
        // Title
        React.createElement(Text, { style: styles.title }, 
          completeData?.project?.name || metadata?.project || 'Room Design Report'
        ),
        
        // Project Information
        React.createElement(View, { style: styles.section },
          React.createElement(Text, { style: styles.sectionTitle }, 'Project Information'),
          React.createElement(Text, { style: styles.text }, 
            `Designer: ${completeData?.project?.designer || metadata?.designer || 'Unknown'}`
          ),
          React.createElement(Text, { style: styles.text }, 
            `Company: ${completeData?.project?.company || 'Unknown'}`
          ),
          React.createElement(Text, { style: styles.text }, 
            `Tool Version: ${completeData?.toolversion || 'Unknown'}`
          ),
          React.createElement(Text, { style: styles.text }, 
            `Units: ${completeData?.units || 'feet'}`
          ),
          React.createElement(Text, { style: styles.text }, 
            `Generated: ${new Date().toLocaleString()}`
          )
        ),

        // Areas/Rooms with visual representations
        ...(completeData.areas ? Object.entries(completeData.areas).map(([areaKey, areaData]: [string, any]) => 
          React.createElement(View, { key: areaKey, style: styles.roomSection },
            React.createElement(Text, { style: styles.roomTitle }, 
              `${areaData.name || areaKey}`
            ),
            
            // Room specifications
            React.createElement(Text, { style: styles.text }, 
              `Dimensions: ${areaData.dimensions?.length || 0} x ${areaData.dimensions?.width || 0} ${completeData?.units || 'units'}`
            ),
            React.createElement(Text, { style: styles.text }, 
              `Ceiling Height: ${areaData.ceilingHeight?.min || areaData.ceilingHeight || 'Unknown'} ${completeData?.units || 'units'}`
            ),
            
            // Floor plan placeholder (this would be replaced with actual SVG/Canvas in full implementation)
            React.createElement(View, { style: styles.floorPlanPlaceholder },
              React.createElement(Text, { style: styles.placeholderText },
                `FLOOR PLAN - ${areaData.dimensions?.length || 0}' x ${areaData.dimensions?.width || 0}'`
              )
            ),

            // Device information with layout details
            areaData.speakers && areaData.speakers.length > 0 ? 
              React.createElement(View, { style: styles.section },
                React.createElement(Text, { style: styles.sectionTitle }, 'Speaker Configuration'),
                React.createElement(Text, { style: styles.deviceInfo }, 
                  `Count: ${areaData.speakers.length} devices`
                ),
                React.createElement(Text, { style: styles.deviceInfo }, 
                  `Model: ${areaData.speakers[0]?.modelName || 'Various models'}`
                ),
                React.createElement(Text, { style: styles.deviceInfo }, 
                  `Coverage: SPL distribution across room`
                )
              ) : null,

            areaData.microphones ? 
              React.createElement(View, { style: styles.section },
                React.createElement(Text, { style: styles.sectionTitle }, 'Microphone Configuration'),
                React.createElement(Text, { style: styles.deviceInfo }, 
                  `Count: ${areaData.microphones.layout?.length || areaData.microphones.count || 0} devices`
                ),
                React.createElement(Text, { style: styles.deviceInfo }, 
                  `Model: ${areaData.microphones.model || 'Unknown model'}`
                ),
                React.createElement(Text, { style: styles.deviceInfo }, 
                  `Coverage: Distance-based pickup pattern`
                )
              ) : null,

            areaData.cameras && areaData.cameras.hasCamera ?
              React.createElement(View, { style: styles.section },
                React.createElement(Text, { style: styles.sectionTitle }, 'Camera Configuration'),
                React.createElement(Text, { style: styles.deviceInfo }, 
                  `Coverage Angle: ${areaData.cameras.coverageAngle || 'Standard'}°`
                ),
                React.createElement(Text, { style: styles.deviceInfo }, 
                  `Field of View: Visual coverage area`
                )
              ) : null,

            // Additional room details
            areaData.table && areaData.table.shape ?
              React.createElement(Text, { style: styles.deviceInfo }, 
                `Table: ${areaData.table.shape} (${areaData.table.dimensions?.length || 0} x ${areaData.table.dimensions?.width || 0})`
              ) : null,

            areaData.materials ?
              React.createElement(Text, { style: styles.deviceInfo }, 
                `Materials - Walls: ${areaData.materials.walls || 'Unknown'}, Ceiling: ${areaData.materials.ceiling || 'Unknown'}`
              ) : null
          )
        ) : [])
      )
    )
  );

  // Generate the PDF
  const pdfBlob = await pdf(React.createElement(VisualPDFDocument)).toBlob();
  
  
  return pdfBlob;
};

/**
 * Generate fallback text PDF when visual export fails
 */
const generateFallbackTextPDF = async (designIds: string[], organizationId: string): Promise<Blob> => {
  const allCompleteDesignData = [];
  
  for (const designId of designIds) {
    try {
      const completeData = await getCompleteDesignData(designId, organizationId);
      const metadata = await getDesignById(designId, organizationId);
      
      allCompleteDesignData.push({
        designId,
        completeData,
        metadata,
        error: completeData ? null : 'No complete data available'
      });
    } catch (error) {
      allCompleteDesignData.push({
        designId,
        completeData: null,
        metadata: await getDesignById(designId, organizationId),
        error: error.message
      });
    }
  }
  
  const pdfContent = generateComprehensivePDFContent(allCompleteDesignData);
  return new Blob([pdfContent], { type: 'application/pdf' });
};

/**
 * Generate enhanced text PDF with more visual-like descriptions
 */
const generateEnhancedTextPDF = async (designData: any[]): Promise<Blob> => {
  const pdfContent = generateComprehensivePDFContent(designData);
  return new Blob([pdfContent], { type: 'application/pdf' });
};

/**
 * Create visual report configuration for PDF generation
 */
const createVisualReportConfiguration = async (completeData: any, metadata: any) => {
  return {
    metadata: {
      title: 'Room Design Report',
      author: completeData.project?.designer || metadata?.designer || 'Room Designer',
      subject: 'Room Design Export',
      keywords: 'room design, floor plan, audio visual',
      creator: 'Room Designer Application'
    },
    body: [
      {
        id: 'header',
        content: [
          {
            element: 'header',
            content: completeData.project?.name || metadata?.project || 'Room Design'
          },
          {
            element: 'headerSubTitle', 
            content: `Generated: ${new Date().toLocaleString()}`
          }
        ]
      },
      {
        id: 'project-info',
        content: [
          {
            element: 'stringField',
            label: 'Project',
            content: completeData.project?.name || metadata?.project || 'Unknown'
          },
          {
            element: 'stringField',
            label: 'Designer',
            content: completeData.project?.designer || metadata?.designer || 'Unknown'
          },
          {
            element: 'valueField',
            label: 'Tool Version',
            content: completeData.toolversion || 'Unknown'
          },
          {
            element: 'valueField',
            label: 'Units',
            content: completeData.units || 'Unknown'
          }
        ]
      }
    ],
    areas: (state: any) => {
      const areas = state.areas || {};
      return Object.entries(areas).map(([areaKey, areaData]: [string, any]) => [
        // Area information section
        {
          id: `area-${areaKey}-info`,
          content: [
            {
              element: 'title',
              content: areaData.name || areaKey
            },
            {
              element: 'valueField',
              label: 'Dimensions',
              content: `${areaData.dimensions?.length || 0} x ${areaData.dimensions?.width || 0} ${state.units || 'units'}`
            },
            {
              element: 'valueField',
              label: 'Ceiling Height', 
              content: `${areaData.ceilingHeight?.min || areaData.ceilingHeight || 'Unknown'} ${state.units || 'units'}`
            }
          ]
        },
        // Visual floor plan section for each device type
        ...(areaData.microphones ? [{
          id: `area-${areaKey}-microphones`,
          content: [
            {
              element: 'subTitle',
              content: 'MICROPHONE LAYOUT'
            },
            {
              element: 'plot',
              areaId: areaKey,
              content: (state: any) => state,
              device: 'microphones',
              constants: { defaultPlots: { microphones: 'micDistance' }, reportPlotElements: ['devices'] }
            }
          ]
        }] : []),
        ...(areaData.speakers ? [{
          id: `area-${areaKey}-speakers`, 
          content: [
            {
              element: 'subTitle',
              content: 'SPEAKER LAYOUT'
            },
            {
              element: 'plot',
              areaId: areaKey,
              content: (state: any) => state,
              device: 'speakers',
              constants: { defaultPlots: { speakers: 'SPL' }, reportPlotElements: ['devices'] }
            }
          ]
        }] : [])
      ]).flat();
    }
  };
};

/**
 * Generate basic PDF content as fallback
 */
const generateBasicPDFContent = (designs: any[]): string => {
  return `%PDF-1.4
1 0 obj<</Type/Catalog/Pages 2 0 R>>endobj
2 0 obj<</Type/Pages/Kids[3 0 R]/Count 1>>endobj  
3 0 obj<</Type/Page/Parent 2 0 R/MediaBox[0 0 612 792]/Contents 4 0 R/Resources<</Font<</F1 5 0 R>>>>>>endobj
4 0 obj<</Length 200>>stream
BT/F1 12 Tf 50 750 Td(Room Design Export Report)Tj 0 -20 Td(Generated: ${new Date().toLocaleString()})Tj 0 -30 Td(Design ID: ${designs[0]?.designId || 'Unknown'})Tj 0 -20 Td(Status: PDF generation in progress...)Tj ET
endstream endobj
5 0 obj<</Type/Font/Subtype/Type1/BaseFont/Helvetica>>endobj
xref 0 6
0000000000 65535 f 0000000010 00000 n 0000000053 00000 n 0000000110 00000 n 0000000252 00000 n 0000000500 00000 n 
trailer<</Size 6/Root 1 0 R>>startxref 565 %%EOF`;
};

/**
 * Create a mock Redux store from complete design data for PDF generation
 * @param allCompleteDesignData - Array of complete design data
 * @returns Mock store object compatible with ExportDocument
 */
const createMockStoreFromDesignData = (allCompleteDesignData: any[]) => {
  const firstDesign = allCompleteDesignData.find(item => item.completeData);
  const designData = firstDesign?.completeData;
  
  // Convert the flattened design data back to Redux store structure
  const mockState = {
    container: {
      // Map flattened areas back to container.areas structure
      areas: designData?.areas || {},
      project: designData?.project || {},
      // Add other container properties as needed
      name: designData?.name || 'Exported Design',
      dimensions: designData?.dimensions,
      materials: designData?.materials,
      budget: designData?.budget
    },
    ui: {
      units: designData?.units || 'feet',
      scale: designData?.scale || 1,
      currentView: 'floor_plan',
      currentDevice: 'microphones',
      showMap: true
    }
  };
  
  // Create a mock store that matches the interface expected by ExportDocument
  return {
    getState: () => mockState
  };
};

/**
 * Create report configuration for visual PDF generation
 * @param allCompleteDesignData - Array of complete design data
 * @returns Report configuration object
 */
const createReportConfiguration = (allCompleteDesignData: any[]) => {
  const firstDesign = allCompleteDesignData.find(item => item.completeData);
  const designData = firstDesign?.completeData;
  const metadata = firstDesign?.metadata;
  
  return {
    metadata: {
      title: 'Room Design Export',
      author: designData?.project?.designer || metadata?.designer || 'Room Designer',
      subject: 'Exported Room Designs',
      keywords: 'room design, floor plan, audio visual',
      creator: 'Room Designer Application',
      producer: 'Room Designer PDF Export'
    },
    body: [
      // Header section
      {
        id: 'header',
        wrap: false,
        content: [
          {
            element: 'title',
            label: 'Room Design Export Report',
            content: null
          },
          {
            element: 'text',
            label: `Generated: ${new Date().toLocaleString()}`,
            content: null
          }
        ]
      },
      // Design information section
      {
        id: 'design-info',
        wrap: false,
        content: [
          {
            element: 'subtitle',
            label: 'Design Information',
            content: null
          },
          {
            element: 'text',
            label: `Project: ${designData?.project?.name || metadata?.project || 'Unknown Project'}`,
            content: null
          },
          {
            element: 'text',
            label: `Designer: ${designData?.project?.designer || metadata?.designer || 'Unknown Designer'}`,
            content: null
          },
          {
            element: 'text',
            label: `Tool Version: ${designData?.toolversion || 'Unknown'}`,
            content: null
          },
          {
            element: 'text',
            label: `Units: ${designData?.units || 'Unknown'}`,
            content: null
          }
        ]
      }
    ],
    // Area-specific sections with visual renderings
    areas: (state: any) => {
      const areas = state.areas || {};
      return Object.entries(areas).map(([areaKey, areaData]: [string, any]) => ({
        id: `area-${areaKey}`,
        wrap: true,
        content: [
          {
            element: 'subtitle',
            label: `Room: ${areaData.name || areaKey}`,
            content: null
          },
          {
            element: 'text',
            label: `Dimensions: ${areaData.dimensions?.length || 0} x ${areaData.dimensions?.width || 0} ${state.units || 'units'}`,
            content: null
          },
          {
            element: 'text', 
            label: `Ceiling Height: ${areaData.ceilingHeight?.min || areaData.ceilingHeight || 'Unknown'} ${state.units || 'units'}`,
            content: null
          },
          // Visual canvas rendering for this area
          {
            element: 'canvas',
            areaId: areaKey,
            device: 'microphones',
            plotProps: {
              source: 'micDistance',
              minLevel: 0,
              maxLevel: 100,
              accuracy: 1
            }
          },
          // Device information
          {
            element: 'text',
            label: `Speakers: ${areaData.speakers?.length || 0} devices`,
            content: null
          },
          {
            element: 'text',
            label: `Microphones: ${areaData.microphones?.layout?.length || areaData.microphones?.count || 0} devices`,
            content: null
          }
        ]
      }));
    }
  };
};

/**
 * Generate comprehensive PDF content from complete design data
 * @param completeDesignData - Array of complete design data objects
 * @returns string - PDF content
 */
const generateComprehensivePDFContent = (completeDesignData: any[]): string => {
  const validDesigns = completeDesignData.filter(item => item.completeData || item.metadata);
  
  // Calculate content length dynamically based on amount of data
  let estimatedLength = 500;
  validDesigns.forEach(item => {
    estimatedLength += 200; // Base per design
    if (item.completeData?.container?.areas) {
      estimatedLength += Object.keys(item.completeData.container.areas).length * 150; // Per area
    }
  });
  
  let content = `%PDF-1.4
1 0 obj
<<
/Type /Catalog
/Pages 2 0 R
>>
endobj

2 0 obj
<<
/Type /Pages
/Kids [3 0 R]
/Count 1
>>
endobj

3 0 obj
<<
/Type /Page
/Parent 2 0 R
/MediaBox [0 0 612 792]
/Contents 4 0 R
/Resources <<
/Font <<
/F1 5 0 R
>>
>>
>>
endobj

4 0 obj
<<
/Length ${estimatedLength}
>>
stream
BT
/F1 12 Tf
50 750 Td
(Comprehensive Room Designs Report) Tj
0 -20 Td
(Generated on: ${new Date().toLocaleString()}) Tj
0 -30 Td
`;

  validDesigns.forEach((item, index) => {
    const designData = item.completeData;
    const metadata = item.metadata;
    
    // The actual data structure is flattened - project info is directly in root
    const projectInfo = designData?.project || {};
    const designName = projectInfo.name || 
                      metadata?.project || 
                      `Design ${index + 1}`;
    
    content += `(===== ${designName} =====) Tj
0 -25 Td
`;

    // Basic Design Information
    content += `(DESIGN INFORMATION:) Tj
0 -20 Td
(  ID: ${item.designId}) Tj
0 -15 Td
(  Tool Version: ${designData?.toolversion || 'Unknown'}) Tj
0 -15 Td
(  Revision: ${designData?.revision || metadata?.revision || 'Unknown'}) Tj
0 -15 Td
(  Units: ${designData?.units || 'Unknown'}) Tj
0 -15 Td
(  Scale: ${designData?.scale || 'Unknown'}) Tj
0 -15 Td
(  Locale: ${designData?.locale || 'Unknown'}) Tj
0 -15 Td
(  Last Modified: ${metadata?.updatedAt ? formatLastModified(metadata.updatedAt) : 'Unknown'}) Tj
0 -20 Td
`;

    // Project Information (flattened structure)
    if (projectInfo && (projectInfo.name || projectInfo.company || projectInfo.designer)) {
      content += `(PROJECT DETAILS:) Tj
0 -15 Td
(  Project Name: ${projectInfo.name || 'Unknown'}) Tj
0 -15 Td
(  Company: ${projectInfo.company || 'Unknown'}) Tj
0 -15 Td
(  Designer: ${projectInfo.designer || metadata?.designer || 'Unknown'}) Tj
0 -20 Td
`;
    }

    // Areas Information (directly in root, not under container)
    if (designData?.areas) {
      const areas = designData.areas;
      const areaCount = Object.keys(areas).length;
      
      content += `(ROOMS/AREAS (${areaCount} total):) Tj
0 -20 Td
`;

      Object.entries(areas).forEach(([areaKey, areaData]: [string, any]) => {
        content += `(  --- ${areaData.name || areaKey} ---) Tj
0 -15 Td
`;
        
        // Room dimensions and basic info
        if (areaData.dimensions) {
          content += `(    Dimensions: ${areaData.dimensions.length || 0} x ${areaData.dimensions.width || 0} ${designData?.units || 'units'}) Tj
0 -12 Td
`;
        }
        
        if (areaData.location) {
          content += `(    Location: (${areaData.location.left || 0}, ${areaData.location.top || 0})) Tj
0 -12 Td
`;
        }
        
        if (areaData.ceilingHeight) {
          content += `(    Ceiling Height: ${areaData.ceilingHeight.min || areaData.ceilingHeight || 'Unknown'} ${designData?.units || 'units'}) Tj
0 -12 Td
`;
        }
        
        if (areaData.areaUsage) {
          content += `(    Usage: ${areaData.areaUsage}) Tj
0 -12 Td
`;
        }

        // Materials
        if (areaData.materials) {
          content += `(    Materials:) Tj
0 -12 Td
(      Walls: ${areaData.materials.walls || 'Unknown'}) Tj
0 -10 Td
(      Ceiling: ${areaData.materials.ceiling || 'Unknown'}) Tj
0 -10 Td
`;
        }

        // Table information
        if (areaData.table && areaData.table.shape) {
          content += `(    Table: ${areaData.table.shape}) Tj
0 -12 Td
`;
          if (areaData.table.dimensions) {
            content += `(      Table Size: ${areaData.table.dimensions.length || 0} x ${areaData.table.dimensions.width || 0}) Tj
0 -10 Td
`;
          }
        }

        // Acoustics
        if (areaData.acoustics) {
          content += `(    Acoustics: ${areaData.acoustics}) Tj
0 -12 Td
`;
        }

        // Device Information
        if (areaData.speakers && areaData.speakers.length > 0) {
          content += `(    Speakers: ${areaData.speakers.length} devices) Tj
0 -12 Td
`;
          content += `(      Model: ${areaData.speakers[0]?.modelName || 'Unknown'}) Tj
0 -10 Td
`;
        }

        if (areaData.microphones) {
          const micCount = areaData.microphones.layout?.length || areaData.microphones.count || 0;
          if (micCount > 0) {
            content += `(    Microphones: ${micCount} devices) Tj
0 -12 Td
(      Model: ${areaData.microphones.model || 'Unknown'}) Tj
0 -10 Td
`;
          }
        }

        if (areaData.cameras && areaData.cameras.hasCamera) {
          content += `(    Cameras: Configured) Tj
0 -12 Td
(      Coverage Angle: ${areaData.cameras.coverageAngle || 'Unknown'}) Tj
0 -10 Td
`;
        }

        content += `0 -15 Td
`;
      });
    } else if (designData?.areaList && designData.areaList.length > 0) {
      // Show area list if areas data is not available
      content += `(AREAS: ${designData.areaList.join(', ')}) Tj
0 -15 Td
`;
    }

    // Quote request status
    if (designData?.requestedQuote !== undefined) {
      content += `(QUOTE REQUESTED: ${designData.requestedQuote ? 'Yes' : 'No'}) Tj
0 -15 Td
`;
    }

    // Error information if data couldn't be fetched
    if (item.error) {
      content += `(ERROR: ${item.error}) Tj
0 -15 Td
`;
    }

    content += `0 -30 Td
`;
  });

  content += `ET
endstream
endobj

5 0 obj
<<
/Type /Font
/Subtype /Type1
/BaseFont /Helvetica
>>
endobj

xref
0 6
0000000000 65535 f 
0000000010 00000 n 
0000000053 00000 n 
0000000110 00000 n 
0000000252 00000 n 
0000000500 00000 n 
trailer
<<
/Size 6
/Root 1 0 R
>>
startxref
565
%%EOF`;

  return content;
};

/**
 * Generate basic PDF content from designs (legacy function)
 * @param designs - Array of design objects
 * @returns string - PDF content
 */
const generatePDFContent = (designs: (Design | null)[]): string => {
  const validDesigns = designs.filter(design => design !== null) as Design[];
  
  let content = `%PDF-1.4
1 0 obj
<<
/Type /Catalog
/Pages 2 0 R
>>
endobj

2 0 obj
<<
/Type /Pages
/Kids [3 0 R]
/Count 1
>>
endobj

3 0 obj
<<
/Type /Page
/Parent 2 0 R
/MediaBox [0 0 612 792]
/Contents 4 0 R
/Resources <<
/Font <<
/F1 5 0 R
>>
>>
>>
endobj

4 0 obj
<<
/Length ${250 + (validDesigns.length * 100)}
>>
stream
BT
/F1 12 Tf
50 750 Td
(Room Designs Export Report) Tj
0 -30 Td
(Generated on: ${new Date().toLocaleDateString()}) Tj
0 -50 Td
`;

  validDesigns.forEach((design, index) => {
    content += `(Design ${index + 1}: ${design.project}) Tj
0 -20 Td
(  Design Type: ${design.designType}) Tj
0 -15 Td
(  Designer: ${design.designer}) Tj
0 -15 Td
(  Revision: ${design.revision}) Tj
0 -15 Td
(  Last Modified: ${formatLastModified(design.updatedAt)}) Tj
0 -30 Td
`;
  });

  content += `ET
endstream
endobj

5 0 obj
<<
/Type /Font
/Subtype /Type1
/BaseFont /Helvetica
>>
endobj

xref
0 6
0000000000 65535 f 
0000000010 00000 n 
0000000053 00000 n 
0000000110 00000 n 
0000000252 00000 n 
0000000500 00000 n 
trailer
<<
/Size 6
/Root 1 0 R
>>
startxref
565
%%EOF`;

  return content;
};

/**
 * Share designs with other users
 * 
 * @param designIds - Array of design IDs to export
 * @param onDownloadSuccess - Optional callback when download is successfully saved
 * @returns Promise<boolean> - Success status
 */
export const exportAsWorkplaceDesigns = async (
  designIds: string[],
  onDownloadSuccess?: () => void
): Promise<boolean> => {
  try {
    const accessToken = STORAGE_SERVICE_API_KEY;
    if (!accessToken) {
      throw new Error('No access token available');
    }

    const apiEndpoint = `${process.env.REACT_APP_STORAGE_SERVICE_URL}/api/v1/designs/exportdesigns`;
    
    const response = await fetch(apiEndpoint, {
      method: 'POST',
      mode: 'cors',
      credentials: 'omit',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        designIds: designIds
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // Get the file blob from the response
    const blob = await response.blob();
    
    // Set filename with timestamp format: YYYYMMDD-HH-mm.bwd
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const filename = `${year}${month}${day}-${hours}-${minutes}.bwd`;

    // Try to use File System Access API first (more reliable for detecting save/cancel)
    if ('showSaveFilePicker' in window) {
      try {
        const fileHandle = await (window as any).showSaveFilePicker({
          suggestedName: filename,
          types: [{
            description: 'Biamp Workplace Design files',
            accept: { 'application/octet-stream': ['.bwd'] }
          }]
        });
        
        const writable = await fileHandle.createWritable();
        await writable.write(blob);
        await writable.close();
        
        // File was successfully saved
        if (onDownloadSuccess) {
          onDownloadSuccess();
        }
        
        return true;
      } catch (error: any) {
        // User canceled the save dialog or other error occurred
        if (error.name === 'AbortError') {
          // User canceled - don't show success toast
          return true;
        }
        // Fall back to traditional download method for other errors
      }
    }
    
    // Fallback: Traditional download method
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    // For traditional downloads, we can't reliably detect save vs cancel
    // So we'll use a more conservative approach with better timing
    if (onDownloadSuccess) {
      let hasShownCallback = false;
      let dialogOpenTime = Date.now();
      let wasBlurred = false;
      
      const showCallbackOnce = () => {
        if (!hasShownCallback) {
          hasShownCallback = true;
          onDownloadSuccess();
          cleanup();
        }
      };
      
      const cleanup = () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        window.removeEventListener('focus', handleWindowFocus);
        window.removeEventListener('blur', handleWindowBlur);
        window.removeEventListener('mousedown', handleMouseDown);
        window.removeEventListener('keydown', handleKeyDown);
      };
      
      const handleWindowBlur = () => {
        wasBlurred = true;
        dialogOpenTime = Date.now();
      };
      
      const handleVisibilityChange = () => {
        if (!document.hidden && wasBlurred) {
          const dialogDuration = Date.now() - dialogOpenTime;
          // More conservative: if dialog was open for more than 2 seconds, likely saved
          if (dialogDuration > 2000) {
            showCallbackOnce();
          } else {
            cleanup();
          }
          wasBlurred = false;
        }
      };
      
      const handleWindowFocus = () => {
        if (wasBlurred) {
          const dialogDuration = Date.now() - dialogOpenTime;
          // More conservative timing
          if (dialogDuration > 2000) {
            showCallbackOnce();
          } else {
            cleanup();
          }
          wasBlurred = false;
        }
      };
      
      // Additional detection for user interaction after dialog
      const handleMouseDown = () => {
        if (wasBlurred) {
          const dialogDuration = Date.now() - dialogOpenTime;
          if (dialogDuration > 1500) {
            showCallbackOnce();
          }
        }
      };
      
      const handleKeyDown = () => {
        if (wasBlurred) {
          const dialogDuration = Date.now() - dialogOpenTime;
          if (dialogDuration > 1500) {
            showCallbackOnce();
          }
        }
      };
      
      // Set up event listeners
      window.addEventListener('blur', handleWindowBlur);
      document.addEventListener('visibilitychange', handleVisibilityChange);
      window.addEventListener('focus', handleWindowFocus);
      window.addEventListener('mousedown', handleMouseDown);
      window.addEventListener('keydown', handleKeyDown);
      
      // Clean up after 15 seconds regardless
      setTimeout(cleanup, 15000);
    }

    return true;
  } catch (error) {
    console.error('Error exporting designs:', error);
    throw error;
  }
};

/**
 * Upload a thumbnail image for a design
 *
 * @param file - Image file to upload
 * @param designId - Design ID to associate the thumbnail with
 * @param organizationId - Organization ID
 * @returns Promise<string> - URL of the uploaded thumbnail
 */
export const uploadThumbnail = async (
  file: File,
  designId: string,
  organizationId: string
): Promise<string> => {
  try {
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      throw new Error('Invalid file type. Please upload a JPEG or PNG image.');
    }

    // Validate file size (5MB limit)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      throw new Error('File size too large. Please upload an image smaller than 5MB.');
    }

    // Get access token for API authentication
    const accessToken = STORAGE_SERVICE_API_KEY;
    if (!accessToken) {
      throw new Error('No access token available');
    }

    // Create FormData for file upload
    const formData = new FormData();
    formData.append('file', file);
    formData.append('designId', designId);
    formData.append('organizationId', organizationId);

    const response = await fetch(`${process.env.REACT_APP_STORAGE_SERVICE_URL}/api/v1/designs/thumbnails`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Upload failed with status: ${response.status}`);
    }

    const data = await response.json();
    return data.thumbnailUrl || data.url || '';
  } catch (error) {
    console.error('Error uploading thumbnail:', error);
    throw error;
  }
};

/**
 * Update a design's thumbnail URL
 * 
 * @param designId - Design ID
 * @param thumbnailUrl - New thumbnail URL
 * @param organizationId - Organization ID
 * @returns Promise<Design> - Updated design
 */
export const updateDesignThumbnail = async (
  designId: string,
  thumbnailUrl: string,
  organizationId: string
): Promise<Design> => {
  try {
    return await updateDesign(designId, { thumbnailUrl }, organizationId);
  } catch (error) {
    console.error('Error updating design thumbnail:', error);
    throw error;
  }
};

/**
 * Import designs from a .bwd file
 * 
 * @param file - .bwd file to import
 * @param organizationId - Organization ID
 * @returns Promise<boolean> - Success status
 */
// Not migrated to httpClient: the client always sends Content-Type: application/json,
// which would break this multipart/form-data upload. Raw fetch is intentional here.
export const importDesigns = async (file: File, organizationId: string): Promise<boolean> => {
  try {
    const accessToken = process.env.REACT_APP_STORAGE_SERVICE_API_KEY;
    if (!accessToken) {
      throw new Error('No access token available');
    }

    // Validate file type
    if (!file.name.toLowerCase().endsWith('.bwd')) {
      throw new Error('Invalid file type. Please select a .bwd file.');
    }

    // Create FormData for file upload
    const formData = new FormData();
    formData.append('archive', file);
    formData.append('orgId', organizationId);

    const apiEndpoint = `${process.env.REACT_APP_STORAGE_SERVICE_URL}/api/v1/designs/importdesigns`;
    
    const response = await fetch(apiEndpoint, {
      method: 'POST',
      mode: 'cors',
      credentials: 'omit',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Import failed with status: ${response.status}`);
    }

    const data = await response.json();
    
    return true;
  } catch (error) {
    console.error('Error importing designs:', error);
    throw error;
  }
};
